import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import createFarmClusters from '../../services/ruutsApi/createFarmClusters'
import deleteFarmClusters from '../../services/ruutsApi/deleteFarmClusters'
import { errors } from '../../services/ruutsApi/errors'
import { getFarmClustersWithMeta } from '../../services/ruutsApi/getFarmClusters'
import { queryKeys } from './queryKeys'

export const CLUSTERS_POLL_INTERVAL_MS = 15_000

export const useFarmClusters = ({ farmId }: { farmId: string | undefined }) => {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: queryKeys.farmClusters(farmId),
    queryFn: ({ signal }) => getFarmClustersWithMeta({ farmId: farmId as string, signal }),
    enabled: !!farmId,
    refetchInterval: ({ state }) =>
      state.data?.meta?.latestRun?.status === 'running' ? CLUSTERS_POLL_INTERVAL_MS : false,
  })

  const latestRun = query.data?.meta?.latestRun ?? null
  const invalidatedRunIdRef = useRef<string | null>(null)
  const observedRunningIdRef = useRef<string | null>(null)

  useEffect(() => {
    // Invalidates on an observed running→succeeded transition for this run id (clock-free), or via
    // a finishedAt vs. dataUpdatedAt check for a return to the screen after the run already landed
    // while unmounted. The ref guards against re-invalidating the same run on a later poll.
    const wasObservedRunning = observedRunningIdRef.current === latestRun?.id
    observedRunningIdRef.current = latestRun?.status === 'running' ? latestRun.id : null

    if (!farmId || latestRun?.status !== 'succeeded' || !latestRun.finishedAt) return
    if (invalidatedRunIdRef.current === latestRun.id) return

    const samplingAreasKey = queryKeys.samplingAreas(farmId)
    if (!wasObservedRunning) {
      const samplingAreasUpdatedAt = queryClient.getQueryState(samplingAreasKey)?.dataUpdatedAt ?? 0
      if (samplingAreasUpdatedAt >= Date.parse(latestRun.finishedAt)) return
    }

    invalidatedRunIdRef.current = latestRun.id
    queryClient.invalidateQueries({ queryKey: samplingAreasKey })
  }, [latestRun?.id, latestRun?.status, latestRun?.finishedAt, farmId, queryClient])

  return {
    data: query.data?.items,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    refetch: query.refetch,
    latestRun,
  }
}

export const useCreateFarmClusters = ({ farmId }: { farmId: string | undefined }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      try {
        await createFarmClusters({ farmId: farmId as string })
      } catch (error) {
        // A 409 means a run is already in progress for this farm (or, pre-existing, that clusters
        // already exist without replace) — the server is the source of truth either way, so let
        // the poll pick it up instead of surfacing a spurious clusterization error for a click
        // that just lost a race.
        if (error instanceof errors.BusinessEntityError && error.status === 409) return
        throw error
      }
    },
    // The run itself resolves in the background; invalidating the clusters query is what
    // surfaces its running state (and, once it lands, its outcome) without a page reload.
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.farmClusters(farmId) }),
    // Consumer renders an inline banner; the global snackbar would be redundant.
    meta: { suppressGlobalError: true },
  })
}

export const useDeleteFarmClusters = ({ farmId }: { farmId: string | undefined }) => {
  const queryClient = useQueryClient()
  const invalidateClusteredEntities = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.farmClusters(farmId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.samplingAreas(farmId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.monitoringSites(farmId) }),
    ])
  return useMutation({
    mutationFn: () => deleteFarmClusters({ farmId: farmId as string }),
    onSuccess: invalidateClusteredEntities,
    // A 404 (already deleted elsewhere), a 409 (a run started elsewhere) or an unknown outcome all
    // leave the panel stale. Not returned, so the rejection never waits on the refetch.
    onError: () => {
      invalidateClusteredEntities()
    },
  })
}
